/**
 * A tela de entrar, em um ou dois passos.
 *
 * Duas frases dela merecem explicação, porque foram escolhidas e não sobraram:
 *
 * "**a mesma senha do seu computador da tesouraria**" está embaixo do campo de senha porque
 * é a primeira dúvida de quem abre isto: "que senha é essa?". Dizer ali evita o telefonema.
 *
 * E o erro é sempre a frase que o servidor mandou, sem a tela inventar nada. Quando a senha
 * não confere, o servidor não conta se foi o e-mail ou a senha, de propósito: contar
 * entregaria a lista de quem tem acesso a quem estiver tentando.
 *
 * **O segundo passo só aparece para quem tem segundo fator ligado**, e o servidor é quem diz
 * isso, depois de conferir a senha. A tela não sabe antes, e não deve saber: perguntar "esta
 * conta tem segundo fator?" antes da senha contaria, a quem está tentando, quais contas estão
 * desprotegidas.
 *
 * **"Esqueci minha senha" é a etapa 6 da parte 4.** A frase que aparece depois de pedir vem do
 * servidor, inteira, e é a mesma exista o e-mail ou não. A tela não pode melhorar essa frase:
 * qualquer diferença entre "mandamos" e "não achamos" seria um jeito de descobrir quem tem
 * acesso ao dinheiro, digitando endereços e olhando a resposta.
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { ErroDoServidor, servidor, type Eu } from '../api'
import { AdicionarATelaInicial } from '../componentes/AdicionarATelaInicial'
import { BaixarOPrograma } from '../componentes/BaixarOPrograma'
import { NOME_DO_SISTEMA } from '../identidade'
import { irPara } from '../rotas'

type Passo =
  | { qual: 'senha' }
  | { qual: 'codigo' }
  /** Pedindo o link de trocar a senha. */
  | { qual: 'esqueci' }
  /** O pedido foi feito. A frase vem do servidor, e é a mesma exista o e-mail ou não. */
  | { qual: 'pedido-feito'; recado: string }

export function Entrar({ aoEntrar }: { aoEntrar: (eu: Eu) => void }): JSX.Element {
  const [passo, setPasso] = useState<Passo>({ qual: 'senha' })
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [entrando, setEntrando] = useState(false)
  const caixaDoCodigo = useRef<HTMLInputElement>(null)

  /*
   * O foco vai para a caixa do código assim que ela aparece, e no celular isso abre o teclado
   * sem mais um toque. Por efeito, e não logo depois de trocar o passo: naquele instante a
   * caixa ainda não foi desenhada, e o foco cairia no vazio. O primeiro retrato mostrou isso.
   */
  useEffect(() => {
    if (passo.qual === 'codigo') caixaDoCodigo.current?.focus()
  }, [passo.qual])

  async function enviarSenha(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setEntrando(true)
    try {
      const fim = await servidor.entrar(email.trim(), senha)
      if (fim.dentro) return aoEntrar(fim)

      // A senha estava certa e falta o código. A senha some da memória da tela aqui mesmo.
      setSenha('')
      setPasso({ qual: 'codigo' })
    } catch (falha) {
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível entrar. Tente de novo.')
      setSenha('')
    } finally {
      setEntrando(false)
    }
  }

  async function pedirOLink(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setEntrando(true)
    try {
      const feito = await servidor.esqueciASenha(email.trim())
      setPasso({ qual: 'pedido-feito', recado: feito.recado })
    } catch (falha) {
      setErro(
        falha instanceof ErroDoServidor
          ? falha.message
          : 'Não foi possível pedir o link. Tente de novo.'
      )
    } finally {
      setEntrando(false)
    }
  }

  async function enviarCodigo(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setEntrando(true)
    try {
      aoEntrar(await servidor.entrarComOCodigo(codigo.trim()))
    } catch (falha) {
      const recado = falha instanceof ErroDoServidor ? falha.message : 'Não foi possível entrar. Tente de novo.'
      setErro(recado)
      setCodigo('')
      // Quando a vez acabou, o caminho é começar pela senha outra vez.
      if (falha instanceof ErroDoServidor && falha.codigo === 'comece_de_novo') {
        setPasso({ qual: 'senha' })
      }
    } finally {
      setEntrando(false)
    }
  }

  return (
    <main className="entrar" data-passo={passo.qual}>
      <h1>{NOME_DO_SISTEMA}</h1>
      <p className="subtitulo">Painel dos pastores</p>

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      {passo.qual === 'senha' && (
        <>
          <form onSubmit={enviarSenha}>
            <label>
              E-mail
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
              />
            </label>

            <label>
              Senha
              <input
                type="password"
                name="senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>

            <button className="principal" type="submit" disabled={entrando}>
              {entrando ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <p className="rodape">
            A senha é a mesma que você usa no computador da tesouraria.
            <br />
            Se ela mudar lá, muda aqui também.
          </p>

          {/*
            Embaixo do rodapé, e discreto. Quem sabe a senha não precisa ver isto em destaque;
            quem não sabe procura exatamente aqui, porque é onde todo site põe.
          */}
          <button
            type="button"
            className="discreto"
            onClick={() => {
              setPasso({ qual: 'esqueci' })
              setSenha('')
              setErro(null)
            }}
          >
            Esqueci minha senha
          </button>

          {/* Só no celular, e só enquanto o painel não foi instalado na tela inicial. */}
          <AdicionarATelaInicial />
        </>
      )}

      {passo.qual === 'esqueci' && (
        <>
          <p className="o-que-fazer">
            Digite o seu e-mail. Mandamos um link para você escolher uma senha nova.
          </p>

          <form onSubmit={pedirOLink}>
            <label>
              E-mail
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
              />
            </label>

            <button className="principal" type="submit" disabled={entrando}>
              {entrando ? 'Mandando...' : 'Mandar o link'}
            </button>
          </form>

          <p className="rodape">
            É o e-mail que está no seu cadastro, o mesmo que você usa para entrar aqui ou o que
            foi anotado no computador da tesouraria.
            <br />
            Se você não tem e-mail no cadastro, peça uma senha nova a quem administra o sistema.
          </p>

          <button
            type="button"
            className="discreto"
            onClick={() => {
              setPasso({ qual: 'senha' })
              setErro(null)
            }}
          >
            Voltar
          </button>
        </>
      )}

      {passo.qual === 'pedido-feito' && (
        <>
          {/*
            A frase vem do servidor e é mostrada como ela veio. Ela diz "SE esse e-mail estiver
            cadastrado", e esse "se" é a proteção: a mesma resposta para quem existe e para quem
            não existe.
          */}
          <p className="recado feito" role="status">{passo.recado}</p>

          {/*
            A caixa de spam já está na frase do servidor, logo acima. Repetir aqui fazia a tela
            dizer a mesma coisa duas vezes em quatro linhas, e o primeiro retrato mostrou.
          */}
          <p className="rodape">
            Abra o e-mail no celular ou no computador e clique no link de dentro. Se nada chegar
            em alguns minutos, confira se digitou o endereço certo.
          </p>

          <button
            type="button"
            className="discreto"
            onClick={() => {
              setPasso({ qual: 'senha' })
              setErro(null)
            }}
          >
            Voltar para entrar
          </button>
        </>
      )}

      {passo.qual === 'codigo' && (
        <>
          <p className="o-que-fazer">
            Agora o código de seis números que o aplicativo do seu celular está mostrando.
          </p>

          <form onSubmit={enviarCodigo}>
            <label>
              Código
              <input
                ref={caixaDoCodigo}
                type="text"
                name="codigo"
                className="codigo"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                /*
                 * `inputMode` numérico abre o teclado de números no celular, e `autoComplete`
                 * com `one-time-code` faz o próprio telefone oferecer o número. Mas o tipo
                 * continua `text`: um código de emergência tem letras, e `type="number"`
                 * engoliria elas sem avisar ninguém.
                 */
                inputMode="numeric"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={20}
                required
              />
            </label>

            <button className="principal" type="submit" disabled={entrando}>
              {entrando ? 'Conferindo...' : 'Entrar'}
            </button>
          </form>

          <p className="rodape">
            O número muda a cada trinta segundos. Se já virou enquanto você digitava, use o
            próximo.
            <br />
            Perdeu o celular? Digite um dos códigos de emergência que você guardou. Cada um abre
            uma entrada só; depois, cadastre o celular novo em Segundo fator.
          </p>

          <button
            type="button"
            className="discreto"
            onClick={() => {
              setPasso({ qual: 'senha' })
              setCodigo('')
              setErro(null)
            }}
          >
            Voltar e entrar com outra conta
          </button>
        </>
      )}

      {/*
        O botão de baixar o programa, fora dos passos.

        Aqui é onde ele mais serve: computador novo, ou notebook formatado, e quem está na frente
        dele não tem o programa para abrir. Abaixo do que a pessoa veio fazer, que é entrar, e
        acima do pé da tela. Sem a variável de ambiente, ele não existe.
      */}
      <BaixarOPrograma />

      {/*
        O caminho para a tela de Sobre, fora dos passos: ele aparece em todos.

        Quem está nesta tela sem conseguir entrar é quem mais precisa dela, e a pergunta
        "o servidor está no ar, ou é a minha internet?" se faz daqui.
      */}
      <p className="pe-da-tela">
        <button className="como-link" type="button" onClick={() => irPara('/sobre')}>
          Sobre este sistema
        </button>
      </p>
    </main>
  )
}
